import * as React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { getFolderFields, getDocumentFields, Field, Document, getDocumentTypes, http } from '../api';
import { getInputType, getOperatorsForField, getOperatorsForType } from './seachpage-helpers';
import { DocumentGrid } from './DocumentGrid';
import { DocumentModals } from './DocumentModals';
import { useDocumentActions } from './useDocumentActions';
import { AddDocumentDropzone } from './AddDocumentDropzone';
import '../styles/app.css';
import { EraserIcon, EyeIcon, EyeOffIcon } from './icons';
import { titleFor } from './search-helpers';
import ValueControl from './ValueControl';

const PANEL_WIDTH_KEY = 'searchPanelWidth';

// Supported textual operators for query encoding
const HUMAN_OPS = ['eq','ne','gt','ge','lt','le','contains','containsnot','startswith','endswith','between','notbetween','empty','notempty'] as const;

type HumanOp = typeof HUMAN_OPS[number];

type Crit = { operator: HumanOp; value: string };

export default function SearchPage({ drawerId, drawerName, metadataOption }: { drawerId: number | null; drawerName: string | null; metadataOption?: 0 | 1 | number }) {
  const [fields, setFields] = useState<Field[]>([]);
  const [criteria, setCriteria] = useState<Record<string, Crit>>({});
  const [results, setResults] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [panelWidth, setPanelWidth] = useState(() => { const saved = localStorage.getItem(PANEL_WIDTH_KEY); return saved ? Math.max(400, Math.min(Number(saved), 700)) : 500; });
  const [loadingFields, setLoadingFields] = useState(false);
  const resizing = useRef(false);
  const fieldsGridRef = useRef<HTMLDivElement>(null);
  const [resultColumns, setResultColumns] = useState<string[]>([]);
  const [showResultColumnSelector, setShowResultColumnSelector] = useState(false);
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [hiddenFields, setHiddenFields] = useState<string[]>([]);
  // local order state for search field selector modal (null = not loaded)
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);
  const [localHiddenSet, setLocalHiddenSet] = useState<Set<string>>(() => new Set());
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pageSize, setPageSize] = useState<number>(25);

  // --- Group collapse state (per drawer) ---
  const groupCollapseKey = useMemo(() => `search_group_collapsed_${drawerId ?? 'none'}`, [drawerId]);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // reload whenever drawer changes
    try {
      const raw = localStorage.getItem(groupCollapseKey);
      if (!raw) { setCollapsedGroups({}); return; }
      const parsed = JSON.parse(raw);
      setCollapsedGroups(parsed && typeof parsed === 'object' ? parsed : {});
    } catch {
      setCollapsedGroups({});
    }
  }, [groupCollapseKey]);

  function toggleGroupCollapsed(label: string) {
    const key = (label ?? '').trim();
    if (!key) return;
    setCollapsedGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(groupCollapseKey, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  // expose panel width via CSS variable (centralized CSS uses --panel-width)
  useEffect(()=>{ document.documentElement.style.setProperty('--panel-width', panelWidth + 'px'); },[panelWidth]);

  // Base set of search fields should be stable and come from configured fields (plus fixed meta/system fields),
  // not from whatever happens to be present in the current result set.
  const orderedValueKeys = useMemo(() => {
    const fieldNames = fields.map(f => f.name);
    const set = new Set(fieldNames);

    const folderNames = fields.filter(f => f.folderfield).map(f => f.name);
    const docNames = fields.filter(f => !f.folderfield).map(f => f.name);

    const ordered: string[] = [];
    // add folder fields
    folderNames.forEach(n => { if (ordered.indexOf(n) === -1) ordered.push(n); });
    // add subfolder meta
    if (ordered.indexOf('subfolder') === -1) ordered.push('subfolder');
    // add document fields
    docNames.forEach(n => { if (ordered.indexOf(n) === -1) ordered.push(n); });

    // ensure metadata keys at the end in desired order if not already present
    ['documentid','createdate'].forEach(m => { if (ordered.indexOf(m) === -1) ordered.push(m); });

    // Also ensure any remaining fieldNames that somehow were missed are included
    fieldNames.forEach(n => { if (ordered.indexOf(n) === -1) ordered.push(n); });

    return ordered;
  }, [fields]);

  // Helpers for criteria access (case-insensitive)
  const getCrit = (name: string): Crit | undefined => criteria[name] || criteria[name.toLowerCase()]

  const documentOnlyFields = useMemo(() => fields.filter(f => !f.folderfield), [fields]);

  // Pass a highlight term down so previews can highlight it (PDF/email/office).
  // Prefer contents (DocumentText), otherwise fall back to DocumentDescription.
  const contentsHighlightText = useMemo(() => {
    const candidates = ['documenttext', 'documentdescription'];
    for (const key of candidates) {
      const crit = getCrit(key);
      const value = (crit?.value ?? '').toString().trim();
      if (!value) continue;
      // Only highlight when using a contains-style operator.
      const op = (crit?.operator ?? '').toString().toLowerCase();
      if (op && op !== 'contains') continue;
      return value;
    }
    return null;
  }, [criteria]);

  // Document Actions Hook
  const documentActions = useDocumentActions({
    fields,
    onDocumentUpdate: (docId, updatedDoc) => {
      setResults(prev => prev.map(d => 
        (d as any).id === docId ? { ...d, ...updatedDoc } : d
      ));
    },
    onDocumentDelete: (docId) => {
      setResults(prev => prev.filter(d => Number((d as any).id ?? (d as any).Id) !== docId));
    }
  });

  // --- URL helpers: read/write criteria as query params ---
  function readCriteriaFromQuery(): Record<string, Crit> {
    const params = new URLSearchParams(window.location.search || '');
    const map: Record<string, Crit> = {};
    params.forEach((v, k) => {
      const key = k.toLowerCase();
      if (key === 'drawerid') return;
      if (v == null) return;
      const dotIdx = v.indexOf('.');
      if (dotIdx > 0) {
        const op = v.substring(0, dotIdx).toLowerCase() as HumanOp;
        const val = v.substring(dotIdx + 1);
        if (HUMAN_OPS.indexOf(op as any) >= 0) map[key] = { operator: op, value: val };
        else map[key] = { operator: 'eq', value: v };
      } else {
        map[key] = { operator: 'eq', value: v };
      }
    });
    return map;
  }

  function writeCriteriaToQuery(drawer: number, c: Record<string, Crit>) {
    const params = new URLSearchParams();
    params.set('drawerid', String(drawer));
    const keys = Object.keys(c).sort() as string[];
    for (const k of keys) {
      const item = c[k];
      if (!item) continue;
      const { operator, value } = item;
      const key = k.toLowerCase();
      // Persist even when value is empty if operator != eq so users can store default operators
      if (!value && operator === 'eq') continue;
      const encoded = operator === 'eq' ? value : `${operator}.${value ?? ''}`;
      params.set(key, encoded);
    }
    const newUrl = `${window.location.pathname}?${params.toString()}#search`;
    if (newUrl !== window.location.href) window.history.replaceState(null, '', newUrl);
  }

  useEffect(() => {
    async function fetchFields() {
      if (!drawerId) { setFields([]); setResultColumns([]); return; }
      setLoadingFields(true);
      try {
        const folderFields = await getFolderFields(drawerId);
        const documentFields = await getDocumentFields(drawerId);
        const combined = [...folderFields, ...documentFields];
        setFields(combined);

        // Default result columns: description first, folder fields + subfolder + meta + document fields
        const defaultCols = [
          'description',
          ...folderFields.map(f=>f.name),
          'subfolder','createdate','documentid',
          ...documentFields.map(f=>f.name)
        ];

        // load saved order per drawer (search page uses 'search' prefix)
        const orderKey = `search_cols_order_${drawerId ?? 'none'}`;
        const savedColsRaw = localStorage.getItem(orderKey);
        if (savedColsRaw) {
          try {
            const savedCols = JSON.parse(savedColsRaw);
            if (Array.isArray(savedCols)) {
              setResultColumns(savedCols.filter((c:string)=> (defaultCols.indexOf(c) >= 0) || combined.some(f=>f.name===c)));
            } else setResultColumns(defaultCols);
          }
          catch { setResultColumns(defaultCols); }
        } else setResultColumns(defaultCols);
      } finally {
        setLoadingFields(false);
      }
    }
    fetchFields().catch(e => setError(e.message));
  }, [drawerId]);

  // Initialize page size from localStorage per-drawer
  useEffect(() => {
    try {
      const key = `search_page_size_${drawerId ?? 'none'}`;
      const raw = localStorage.getItem(key);
      const v = raw ? Number(raw) : 25;
      setPageSize(Number.isFinite(v) && v > 0 ? v : 25);
    } catch {
      setPageSize(25);
    }
  }, [drawerId]);

  useEffect(() => {
    if (fields.length && resultColumns.length) {
      // Ensure description first, then folder fields, then subfolder, then other meta, then document fields order is kept
      const folder = fields.filter(f=>f.folderfield).map(f=>f.name);
      const docs = fields.filter(f=>!f.folderfield).map(f=>f.name);
      const meta = ['description','createdate','documentid','subfolder'];
      // Build desired order: description, folder fields, subfolder, other meta, document fields
      const metaOther = meta.filter(m => m !== 'description' && m !== 'subfolder');
      const order = new Map(['description', ...folder, 'subfolder', ...metaOther, ...docs].map((n,i)=>[n,i] as const));
      setResultColumns(cols => cols.slice().sort((a,b) => (order.get(a)??9999)-(order.get(b)??9999)));
    }
  }, [fields]);

  useEffect(() => { getDocumentTypes()?.then(setDocumentTypes).catch(()=>setDocumentTypes([])); }, [drawerId]);
  useEffect(() => { setResults([]); setContinuationToken(null); setCriteria({}); setSortField(null); setSortDir('desc'); }, [drawerId]);

  // Load criteria from query string if present
  useEffect(()=>{
    if(!drawerId) return;
    const c = readCriteriaFromQuery();
    if(Object.keys(c).length){
      setCriteria(c);
      // Keep current sort (if any); criteria override ensures we don't depend on state update timing.
      performSearch(undefined, undefined, true, c);
      return;
    }

    // Default behavior: when switching drawers (or first landing) with no criteria in the URL,
    // perform a default search so the document list isn't empty.
    setCriteria({});
    performSearch(null, undefined, true, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[drawerId]);

  function hiddenKey(id:number|null){ return `searchFieldHidden_${id??'none'}`; }

  // keys used for persisting search panel order & hidden columns (new key uses storagePrefix style)
  function searchOrderKey(id:number|null){ return `searchfields_cols_order_${id??'none'}`; }
  function searchHiddenKey(id:number|null){ return `searchfields_cols_hidden_${id??'none'}`; }

  // Consolidated visibility / migration logic:
  // Preferences are stored per-drawer in `searchFieldHidden_{drawer}` (hidden list).
  // For backwards compatibility, migrate from legacy `searchFieldVisibility` (a visible list)
  // if present and no per-drawer hidden key exists.
  useEffect(() => {
    if (!fields.length) return;
    // Prefer new per-prefix hidden key for search panel, else fallback to legacy per-drawer key or legacy visible list
    const newHiddenKey = searchHiddenKey(drawerId);
    const legacyHiddenKey = hiddenKey(drawerId);
    const savedNewHidden = localStorage.getItem(newHiddenKey);
    if (savedNewHidden) {
      try{ const p = JSON.parse(savedNewHidden); setHiddenFields(Array.isArray(p)? p.filter((f:string)=> fields.some(x=>x.name===f)) : []); }
      catch{ setHiddenFields([]); }
      return;
    }
    const savedLegacyHidden = localStorage.getItem(legacyHiddenKey);
    if (savedLegacyHidden) {
      try{
        const p = JSON.parse(savedLegacyHidden);
        const arr = Array.isArray(p)? p.filter((f:string)=> fields.some(x=>x.name===f)) : [];
        setHiddenFields(arr);
        // migrate legacy hidden into new key for future use
        try{ localStorage.setItem(newHiddenKey, JSON.stringify(arr)); } catch {}
      }
      catch{ setHiddenFields([]); }
      return;
    }

    // No hidden key found, try migrating from legacy visible list 'searchFieldVisibility'
    const legacyVisible = localStorage.getItem('searchFieldVisibility');
    if (legacyVisible) {
      try{
        const parsed = JSON.parse(legacyVisible);
        if (Array.isArray(parsed)){
          const allNames = fields.map(f=>f.name);
          const hidden = allNames.filter(n => parsed.indexOf(n) === -1);
          setHiddenFields(hidden.filter(h => fields.some(f=>f.name===h)));
          try{ localStorage.setItem(legacyHiddenKey, JSON.stringify(hidden)); } catch {}
        } else setHiddenFields([]);
      } catch { setHiddenFields([]); }
    } else setHiddenFields([]);
  }, [fields, drawerId]);

  // Reset columns handler: clear stored order and hidden fields so defaults from server are used
  function handleResetColumns() {
    if (!drawerId) return;
    try {
      const ok = searchOrderKey(drawerId);
      const hk = searchHiddenKey(drawerId);
      localStorage.removeItem(ok);
      localStorage.removeItem(hk);
    } catch {}
    // reset local state to defaults based on current fields
    const folderFields = fields.filter(f => f.folderfield);
    const documentFields = fields.filter(f => !f.folderfield);
    const defaultCols = [
      'description',
      ...folderFields.map(f=>f.name),
      'subfolder','createdate','documentid',
      ...documentFields.map(f=>f.name)
    ];
    setResultColumns(defaultCols);
    setHiddenFields([]);
    // also clear localOrder and localHiddenSet
    setLocalOrder(null);
    setLocalHiddenSet(new Set());
  }

  // --- Search field selector modal state & persistence ---
  useEffect(() => {
    // load saved order & hidden for modal whenever drawer or orderedValueKeys change
    if (!drawerId) return;
    const ok = searchOrderKey(drawerId);
    const hk = searchHiddenKey(drawerId);
    try {
      const rawOrder = localStorage.getItem(ok);
      const rawHidden = localStorage.getItem(hk) || localStorage.getItem(hiddenKey(drawerId));
      let parsedOrder: string[] | null = null;
      let parsedHidden: string[] | null = null;
      if (rawOrder) { const p = JSON.parse(rawOrder); if (Array.isArray(p)) parsedOrder = p; }
      if (rawHidden) { const p = JSON.parse(rawHidden); if (Array.isArray(p)) parsedHidden = p; }

      if (parsedOrder) {
        const set = new Set(orderedValueKeys);
        const filtered = parsedOrder.filter(c => set.has(c));
        const missing = orderedValueKeys.filter(c => filtered.indexOf(c) === -1);
        setLocalOrder([...filtered, ...missing]);
      } else setLocalOrder(null);

      if (parsedHidden) {
        // never allow documenttype to be hidden
        const filtered = parsedHidden.filter(c => c !== 'documenttype' && orderedValueKeys.indexOf(c) !== -1);
        setLocalHiddenSet(new Set(filtered));
      } else {
        setLocalHiddenSet(new Set(hiddenFields.filter(h => h !== 'documenttype')));
      }
    } catch { setLocalOrder(null); setLocalHiddenSet(new Set(hiddenFields)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerId, JSON.stringify(orderedValueKeys), hiddenFields]);

  function persistSearchOrder(arr: string[]) { try{ localStorage.setItem(searchOrderKey(drawerId), JSON.stringify(arr)); } catch{} }
  // Only persist to new searchfields key (don't write legacy hidden key anymore)
  function persistSearchHidden(arr: string[]) { try{ localStorage.setItem(searchHiddenKey(drawerId), JSON.stringify(arr)); } catch{} }

  // compute base order for search form (use localOrder if present otherwise orderedValueKeys)
  const searchBaseOrder = useMemo(() => {
    if (localOrder && localOrder.length) return localOrder.slice();
    return orderedValueKeys.slice();
  }, [localOrder, orderedValueKeys]);

  // drag handlers for modal
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);
  function onDragStart(e: React.DragEvent, idx: number) { e.dataTransfer.setData('text/plain', String(idx)); try{ e.dataTransfer.effectAllowed = 'move'; }catch{} }
  function onDragOverItem(e: React.DragEvent, idx: number) { e.preventDefault(); setDragOverIndex(idx); }
  function onDropItem(e: React.DragEvent, idx: number) {
    e.preventDefault(); const s = e.dataTransfer.getData('text/plain'); const from = Number(s);
    const curOrder = searchBaseOrder.slice(); if (isNaN(from) || from < 0 || from >= curOrder.length) { setDragOverIndex(null); return; }
    const removed = curOrder.splice(from, 1);
    if (removed.length === 0) { setDragOverIndex(null); return; }
    const item = removed[0] as string; curOrder.splice(idx, 0, item);
    setLocalOrder(curOrder); persistSearchOrder(curOrder); setDragOverIndex(null);
  }

  function toggleSearchHidden(name: string) {
    // never allow documenttype to be hidden
    if (name === 'documenttype') return;
    const next = new Set(localHiddenSet);
    if (next.has(name)) next.delete(name); else next.add(name);
    setLocalHiddenSet(next);
    const arr = Array.from(next);
    persistSearchHidden(arr);
    // also sync main hiddenFields state so form rendering updates immediately
    setHiddenFields(arr);
  }

  // visible fields are derived from hiddenFields when needed by UI

  // header click handler (ensure in scope before JSX)
  function onHeaderClick(col:string){ let dir:'asc'|'desc'='asc'; if(sortField===col) dir = sortDir==='asc'?'desc':'asc'; setSortField(col); setSortDir(dir); performSearch(col,dir,true); }
  function handleChange(field:string,type:'operator'|'value',val:string){
    const key = field.toLowerCase();
    setCriteria(prev => {
      const existing = prev[key] ?? ({ operator: 'eq' as HumanOp, value: '' } as Crit);
      if (type === 'operator') {
        const newOp = val as HumanOp;
        let newVal = existing.value ?? '';
        // when switching away from between, keep only the first part (before ~)
        if (newOp !== 'between' && newVal.indexOf('~') >= 0) {
          newVal = newVal.split('~')[0] ?? '';
        }
        // empty/notempty operators should not carry a value
        if (newOp === 'empty' || newOp === 'notempty') newVal = '';
        return { ...prev, [key]: { operator: newOp, value: newVal } };
      }
      // value update
      return { ...prev, [key]: { operator: existing.operator ?? 'eq', value: val } };
    });
  }

  function addMRU(drawer:number|null,field:string,val:string){ if(!val) return; const key=`searchMRU_${drawer??'none'}_${field}`; let arr:string[]=[]; try{arr=JSON.parse(localStorage.getItem(key)||'[]')||[];}catch{} arr=[val,...arr.filter(a=>a!==val)].slice(0,10); localStorage.setItem(key,JSON.stringify(arr)); }
  function getMRU(drawer:number|null,field:string){ try{ return JSON.parse(localStorage.getItem(`searchMRU_${drawer??'none'}_${field}`)||'[]')||[]; }catch{return [];} }
  function handleInputBlur(field:string,value:string){ addMRU(drawerId,field,value); }
  function handleInputKeyDown(field:string,value:string,e:React.KeyboardEvent<HTMLInputElement>){ if(e.key==='Enter') addMRU(drawerId,field,value); }

  function handleClearSearch() {
    setCriteria({});
    setResults([]);
    setContinuationToken(null);
    setSortField(null);
    setSortDir('desc');
    setError(null);
    setLoading(false);
    setLoadingMore(false);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    if (drawerId) {
      try { writeCriteriaToQuery(drawerId, {}); } catch {}
    }
  }

  // --- reusable search executor (initial or resort) ---
  async function performSearch(newSortField?:string|null,newSortDir?:'asc'|'desc',reset=true, criteriaOverride?: Record<string, Crit>){
    if(!drawerId) return; if(reset){ setContinuationToken(null); if(scrollRef.current) scrollRef.current.scrollTop=0; }
    setLoading(true); setLoadingMore(false); setError(null);
    try {
      const sourceCriteria = criteriaOverride ?? criteria;
      const apiCriteria:Record<string,any>={}; for(const k in sourceCriteria){ const v = sourceCriteria[k]; if(v && (v.value !== undefined || v.operator) && v.operator) apiCriteria[k]={operator:v.operator,value:v.value}; }
      let filter=`drawer eq ${drawerId}`;
      for(const f in apiCriteria){ const operator = apiCriteria[f].operator; const value = apiCriteria[f].value; 
        // operators empty/notempty do not take a value
        if (operator === 'empty' || operator === 'notempty') { filter += ` and ${f} ${operator}`; continue; }
        // between: ensure values separated by ~ for api
        if (operator === 'between') { let v = value ?? ''; filter += ` and ${f} ${operator} ${v}`; continue; }
        filter += ` and ${f} ${operator} ${value}`;
      }
      // `newSortField` semantics:
      // - `undefined`: keep current sortField
      // - `null`: clear sortField
      const effectiveSortField = newSortField === undefined ? sortField : newSortField;
      const effectiveSortDir = newSortDir === undefined ? sortDir : newSortDir;
      const body={ filter, take: pageSize, orderBy: effectiveSortField ? `${effectiveSortField} ${effectiveSortDir}` : undefined };

      // Write query parameters for shareable link (preserve default operators)
      try{ writeCriteriaToQuery(drawerId, sourceCriteria); }catch{}

      const data= await http<any>('/api/document/search',{method:'POST',json:body}); const apiValues= data.Values??data.values??[];
      setResults(apiValues); setContinuationToken(data.ContinuationToken??data.continuationToken??null);
    } catch(e:any){ setError(e.message);} finally{ setLoading(false);} }

    async function loadMore() {
        if (!drawerId || !continuationToken || loadingMore) return; const sc = scrollRef.current; const prev = sc?.scrollTop ?? 0; setLoadingMore(true); try {
            const apiCriteria: Record<string, any> = {};
            for (const k in criteria) {
                const v = criteria[k];
                if (v && (v.value !== undefined || v.operator) && v.operator) apiCriteria[k] = { operator: v.operator, value: v.value };
            }
            let filter = `drawer eq ${drawerId}`;
            for (const f in apiCriteria) {
                const operator = apiCriteria[f].operator;
                const value = apiCriteria[f].value; if (operator === 'empty' || operator === 'notempty') {
                    filter += ` and ${f} ${operator}`; continue;
                }
                if (operator === 'between') {
                    let v = value ?? ''; v = v.replace(/\s+to\s+/i, '~').replace(/\s*[-]\s*/g, '~').replace(/\s*,\s*/g, '~');
                    filter += ` and ${f} ${operator} ${v}`;
                    continue;
                }
                filter += ` and ${f} ${operator} ${value}`;
            }
            const body = { filter, take: pageSize, orderBy: sortField ? `${sortField} ${sortDir}` : undefined, continuationToken };
            const data = await http<any>('/api/document/search', { method: 'POST', json: body });
            const apiValues = data.Values ?? data.values ?? [];
            setResults(r => [...r, ...apiValues]);
            setContinuationToken(data.ContinuationToken ?? data.continuationToken ?? null);
            requestAnimationFrame(() => { if (sc) sc.scrollTop = prev; });
        } catch (e: any) { setError(e.message); } finally {
            setLoadingMore(false);
        }
    }

  // Handler invoked by DocumentGrid when user changes page size
  function handlePageSizeChange(size: number) {
    try { const key = `search_page_size_${drawerId ?? 'none'}`; localStorage.setItem(key, String(size)); } catch {}
    setPageSize(size);
    // do NOT call performSearch directly here to avoid reentrancy loops; an effect will run on pageSize change
  }

  // When pageSize changes, re-run the search once (skip initial mount)
  const _pageSizeFirstRun = React.useRef(true);
  useEffect(() => {
    if (_pageSizeFirstRun.current) { _pageSizeFirstRun.current = false; return; }
    // reset results and run search with new page size
    performSearch(undefined, undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  useEffect(()=>{ localStorage.setItem(PANEL_WIDTH_KEY,String(panelWidth)); },[panelWidth]);
  function handleMouseDown(e:React.MouseEvent){ e.preventDefault(); e.stopPropagation(); resizing.current=true; document.body.style.cursor='col-resize'; document.body.style.userSelect='none'; }
  useEffect(()=>{ function move(e:MouseEvent){ if(resizing.current) setPanelWidth(Math.max(250,Math.min(e.clientX,700))); } function up(){ resizing.current=false; document.body.style.cursor=''; document.body.style.userSelect=''; } window.addEventListener('mousemove',move); window.addEventListener('mouseup',up); return()=>{ window.removeEventListener('mousemove',move); window.removeEventListener('mouseup',up); }; },[]);

  function handleResultColumnToggle(col:string){ setResultColumns(p=> p.indexOf(col)!==-1? p.filter(f=>f!==col): [...p,col]); }
  function handleFieldToggle(fieldName:string){ setHiddenFields(p=> p.indexOf(fieldName)!==-1? p.filter(f=>f!==fieldName): [...p,fieldName]); }

  // Meta search fields configuration (subfolder handled separately)
  const metaSearchFields: {name: string; title: string; type: 'text'|'date'|'number'}[] = [
    { name: 'description', title: 'Description', type: 'text' },
    { name: 'createdate', title: 'Create Date', type: 'date' },
    { name: 'documentid', title: 'Document Id', type: 'number' }
  ];

  const folderFields = fields.filter(f => f.folderfield);
  const docFields = fields.filter(f => !f.folderfield);

  return (
    <div className="search-page" style={{ height: 'calc(90vh / var(--ui-scale,1))' }}>
      <div className="search-main">
        <form className="search-form" onSubmit={e=>{ e.preventDefault(); performSearch(); }}>
          <div className="header-row">
            <h3>Search Options</h3>
            <button type="button" onClick={() => setShowFieldSelector(prev => !prev)} className="field-selector-btn" title={showFieldSelector? 'Hide Field Selector':'Show Field Selector'}>
              {showFieldSelector ? <EyeOffIcon/> : <EyeIcon/>}
            </button>
          </div>
          {showFieldSelector && (
            <div className="col-settings-modal">
              <div className="col-settings-backdrop" onClick={() => setShowFieldSelector(false)} style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1990 }} />
              <div className="col-settings-panel" style={{ position: 'fixed', left: Math.max(8, Math.round((window.innerWidth - 420) / 2)), top: Math.max(8, Math.round((window.innerHeight - 360) / 2)), width: 420, zIndex: 2000, backgroundColor: '#fff', borderRadius: 6, boxShadow: '0 6px 24px rgba(0,0,0,0.3)' }} onClick={e=> e.stopPropagation()}>
                <div className="col-settings-header" style={{ cursor: 'move', padding: '8px 12px', borderBottom: '1px solid #ddd' }}>
                  <strong>Configure Search Fields</strong>
                </div>
                <div style={{ maxHeight: 360, overflowY: 'auto', padding: 8 }}>
                  <div className="col-settings-list">
                    {searchBaseOrder.filter(c=> c !== 'documenttype').map((col, idx) => {
                       const label = titleFor(col, fields);
                       const hidden = localHiddenSet.has(col);
                       return (
                         <div key={col} className={`col-settings-item ${dragOverIndex === idx ? 'drag-over' : ''}`} draggable onDragStart={(e)=> onDragStart(e, idx)} onDragOver={(e)=> onDragOverItem(e, idx)} onDrop={(e)=> onDropItem(e, idx)} style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                           <span style={{ marginRight: 8 }}>{'\u2630'}</span>
                           <label style={{ flex: 1, userSelect: 'none' }}>
                             <input type="checkbox" checked={!hidden} onChange={() => toggleSearchHidden(col)} />
                             <span style={{ marginLeft: 8 }}>{label}</span>
                           </label>
                         </div>
                       );
                     })}
                  </div>
                </div>
                <div className="col-settings-actions" style={{ padding: 8, textAlign: 'right' }}>
                  <button type="button" onClick={() => { handleResetColumns(); setShowFieldSelector(false); }} className="btn">Reset</button>
                  <button type="button" onClick={() => setShowFieldSelector(false)} className="btn btn-primary" style={{ marginLeft: 8 }}>Close</button>
                </div>
              </div>
            </div>
          )}
          <div ref={fieldsGridRef} className="fields-grid-wrapper relative">
            {loadingFields && fields.length === 0 ? (
              <div>Loading...</div>
            ) : (
              <>
                {/* Default/main groupbox: everything until the first group header */}
                <div className="fields-groupbox fields-groupbox-default">
                  <div className="fields-grid">
                    {/* System-defined field: Document contents (DocumentText) */}
                    <span className="label-strong">Contents</span>
                    <span />
                    <div className="relative">
                      <ValueControl
                        name={getCrit('documenttext') ? 'documenttext' : 'DocumentText'}
                        drawerId={drawerId}
                        getCrit={getCrit}
                        handleChange={(name, which, value) => {
                          // Force operator to always be 'contains' for this system field.
                          if (which === 'value') {
                            handleChange(name, 'operator', 'contains');
                          }
                          handleChange(name, which, value);
                        }}
                        handleInputBlur={handleInputBlur}
                        handleInputKeyDown={handleInputKeyDown}
                        getMRU={getMRU}
                      />
                    </div>

                    {/* Document type selector (always present) */}
                    <span className="label-strong">Document type</span><span />
                    <div className="relative">
                      <select value={getCrit('documenttype')?.value||''} onChange={e=> handleChange('documenttype','value',e.target.value)} className="input-default">
                        <option value=''>-- All types --</option>
                        {documentTypes.map(dt=> <option key={dt.id} value={dt.id}>{dt.documentTypeName}</option>)}
                      </select>
                    </div>

                    {/* System-defined field: Document description */}
                    <span className="label-strong">Document description</span>
                    <select
                      value={getCrit('documentdescription')?.operator || getCrit('documentDescription')?.operator || 'contains'}
                      onChange={e => {
                        // Prefer lowercase key if present; fall back to camelCase for legacy/back-end variants.
                        const key = getCrit('documentdescription') ? 'documentdescription' : 'documentDescription';
                        handleChange(key, 'operator', e.target.value);
                      }}
                      className="input-default"
                    >
                      {getOperatorsForType('text').map(op=> <option key={op.value} value={op.value}>{op.label}</option>)}
                    </select>
                    <div className="relative">
                      <ValueControl
                        name={getCrit('documentdescription') ? 'documentdescription' : 'documentDescription'}
                        drawerId={drawerId}
                        getCrit={getCrit}
                        handleChange={(name, which, value) => {
                          // UI defaults to 'contains' when no criterion exists yet.
                          // Preserve that default when the user starts typing, instead of
                          // implicitly creating the criterion with '='.
                          if (which === 'value') {
                            const currentOp = getCrit(name)?.operator;
                            if (!currentOp) handleChange(name, 'operator', 'contains');
                          }
                          handleChange(name, which, value);
                        }}
                        handleInputBlur={handleInputBlur}
                        handleInputKeyDown={handleInputKeyDown}
                        getMRU={getMRU}
                      />
                    </div>

                    {/* The rest of the default/un-grouped fields are appended below via the renderer */}
                    {(() => {
                      const getGroupMarker = (fieldName: string) => {
                        const fldForGroup = fields.find(f => f.name === fieldName);
                        const marker = ((fldForGroup as any)?.group as string | undefined) ?? '';
                        const trimmed = marker.trim();
                        return trimmed || null;
                      };

                      const defaultRows: React.ReactNode[] = [];
                      for (const name of searchBaseOrder) {
                        if (hiddenFields.indexOf(name) !== -1) continue;
                        if (name === 'documenttype') continue;
                        if (name === 'documenttext' || name === 'DocumentText') continue;
                        if (name === 'documentdescription' || name === 'documentDescription') continue;

                        // stop once we hit the first group marker
                        const marker = getGroupMarker(name);
                        if (marker) break;

                        if (name === 'subfolder') {
                          defaultRows.push(
                            <React.Fragment key={name}>
                              <span className="label-strong">Subfolder</span>
                              <select value={getCrit('subfolder')?.operator||'eq'} onChange={e=> handleChange('subfolder','operator',e.target.value)} className="input-default">
                                {getOperatorsForType('text').map(op=> <option key={op.value} value={op.value}>{op.label}</option>)}
                              </select>
                              <div className="relative">
                                <ValueControl name={'subfolder'} drawerId={drawerId} getCrit={getCrit} handleChange={handleChange} handleInputBlur={handleInputBlur} handleInputKeyDown={handleInputKeyDown} getMRU={getMRU} />
                              </div>
                            </React.Fragment>
                          );
                          continue;
                        }

                        const meta = metaSearchFields.find(m => m.name === name);
                        if (meta) {
                          defaultRows.push(
                            <React.Fragment key={name}>
                              <span className="label-strong">{meta.title}</span>
                              <select value={getCrit(meta.name)?.operator||'eq'} onChange={e=> handleChange(meta.name,'operator',e.target.value)} className="input-default">
                                {getOperatorsForType(meta.type).map(op=> <option key={op.value} value={op.value}>{op.label}</option>)}
                              </select>
                              <div className="relative">
                                <ValueControl name={meta.name} drawerId={drawerId} getCrit={getCrit} handleChange={handleChange} handleInputBlur={handleInputBlur} handleInputKeyDown={handleInputKeyDown} getMRU={getMRU} />
                              </div>
                            </React.Fragment>
                          );
                          continue;
                        }

                        const fld = fields.find(f => f.name === name);
                        if (fld) {
                          defaultRows.push(
                            <React.Fragment key={fld.name}>
                              <span className="label-strong">{fld.title}</span>
                              <select value={getCrit(fld.name)?.operator||'eq'} onChange={e=> handleChange(fld.name,'operator',e.target.value)} className="input-default">
                                {getOperatorsForField(fld).map(op=> <option key={op.value} value={op.value}>{op.label}</option>)}
                              </select>
                              <div className="relative">
                                <ValueControl field={fld} name={fld.name} drawerId={drawerId} getCrit={getCrit} handleChange={handleChange} handleInputBlur={handleInputBlur} handleInputKeyDown={handleInputKeyDown} getMRU={getMRU} />
                              </div>
                            </React.Fragment>
                          );
                          continue;
                        }

                        defaultRows.push(
                          <React.Fragment key={name}>
                            <span className="label-strong">{titleFor(name, fields)}</span>
                            <select value={getCrit(name)?.operator||'eq'} onChange={e=> handleChange(name,'operator',e.target.value)} className="input-default">
                              {getOperatorsForType('text').map(op=> <option key={op.value} value={op.value}>{op.label}</option>)}
                            </select>
                            <div className="relative">
                              <ValueControl name={name} drawerId={drawerId} getCrit={getCrit} handleChange={handleChange} handleInputBlur={handleInputBlur} handleInputKeyDown={handleInputKeyDown} getMRU={getMRU} />
                            </div>
                          </React.Fragment>
                        );
                      }

                      return defaultRows;
                    })()}
                  </div>
                </div>

                {/* Render all search fields in saved order, respecting hiddenFields, grouped in bordered group boxes */}
                {(() => {
                  // IMPORTANT: field.group is treated as a *start-of-group marker*.
                  // We'll render group boxes in-place (streaming) to guarantee stable order.

                  const getGroupMarker = (fieldName: string) => {
                    const fldForGroup = fields.find(f => f.name === fieldName);
                    const marker = ((fldForGroup as any)?.group as string | undefined) ?? '';
                    const trimmed = marker.trim();
                    return trimmed || null;
                  };

                  type GroupBlock = { key: string; label: string; rows: React.ReactNode[] };

                  const blocks: GroupBlock[] = [{ key: '__default__', label: '', rows: [] }];
                  let active = blocks[0];

                  const openGroup = (label: string, markerFieldName: string) => {
                    const key = `group_${label}_${markerFieldName}`;
                    const next: GroupBlock = { key, label, rows: [] };
                    blocks.push(next);
                    active = next;
                  };

                  const addRow = (row: React.ReactNode) => {
                    active.rows.push(row);
                  };

                  for (const name of searchBaseOrder) {
                    if (hiddenFields.indexOf(name) !== -1) continue;
                    if (name === 'documenttype') continue;
                    if (name === 'documentdescription' || name === 'documentDescription') continue;

                    const marker = getGroupMarker(name);
                    if (marker) openGroup(marker, name);

                    if (name === 'subfolder') {
                      addRow(
                        <React.Fragment key={name}>
                          <span className="label-strong">Subfolder</span>
                          <select value={getCrit('subfolder')?.operator||'eq'} onChange={e=> handleChange('subfolder','operator',e.target.value)} className="input-default">
                            {getOperatorsForType('text').map(op=> <option key={op.value} value={op.value}>{op.label}</option>)}
                          </select>
                          <div className="relative">
                            <ValueControl name={'subfolder'} drawerId={drawerId} getCrit={getCrit} handleChange={handleChange} handleInputBlur={handleInputBlur} handleInputKeyDown={handleInputKeyDown} getMRU={getMRU} />
                          </div>
                        </React.Fragment>
                      );
                      continue;
                    }

                    const meta = metaSearchFields.find(m => m.name === name);
                    if (meta) {
                      addRow(
                        <React.Fragment key={name}>
                          <span className="label-strong">{meta.title}</span>
                          <select value={getCrit(meta.name)?.operator||'eq'} onChange={e=> handleChange(meta.name,'operator',e.target.value)} className="input-default">
                            {getOperatorsForType(meta.type).map(op=> <option key={op.value} value={op.value}>{op.label}</option>)}
                          </select>
                          <div className="relative">
                            <ValueControl name={meta.name} drawerId={drawerId} getCrit={getCrit} handleChange={handleChange} handleInputBlur={handleInputBlur} handleInputKeyDown={handleInputKeyDown} getMRU={getMRU} />
                          </div>
                        </React.Fragment>
                      );
                      continue;
                    }

                    const fld = fields.find(f => f.name === name);
                    if (fld) {
                      addRow(
                        <React.Fragment key={fld.name}>
                          <span className="label-strong">{fld.title}</span>
                          <select value={getCrit(fld.name)?.operator||'eq'} onChange={e=> handleChange(fld.name,'operator',e.target.value)} className="input-default">
                            {getOperatorsForField(fld).map(op=> <option key={op.value} value={op.value}>{op.label}</option>)}
                          </select>
                          <div className="relative">
                            <ValueControl field={fld} name={fld.name} drawerId={drawerId} getCrit={getCrit} handleChange={handleChange} handleInputBlur={handleInputBlur} handleInputKeyDown={handleInputKeyDown} getMRU={getMRU} />
                          </div>
                        </React.Fragment>
                      );
                      continue;
                    }

                    addRow(
                      <React.Fragment key={name}>
                        <span className="label-strong">{titleFor(name, fields)}</span>
                        <select value={getCrit(name)?.operator||'eq'} onChange={e=> handleChange(name,'operator',e.target.value)} className="input-default">
                          {getOperatorsForType('text').map(op=> <option key={op.value} value={op.value}>{op.label}</option>)}
                        </select>
                        <div className="relative">
                          <ValueControl name={name} drawerId={drawerId} getCrit={getCrit} handleChange={handleChange} handleInputBlur={handleInputBlur} handleInputKeyDown={handleInputKeyDown} getMRU={getMRU} />
                        </div>
                      </React.Fragment>
                    );
                  }

                  // Only return explicit group boxes; default rows are rendered above in the single default groupbox.
                  return blocks
                    .filter(b => b.key !== '__default__' && b.rows.length > 0)
                    .map(b => (
                      <div key={b.key} className={b.key === '__default__' ? 'fields-groupbox fields-groupbox-default' : 'fields-groupbox'}>
                        {b.key !== '__default__' && b.label && (
                          <div
                            className="fields-groupbox-title"
                            role="button"
                            tabIndex={0}
                            onClick={() => toggleGroupCollapsed(b.label)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleGroupCollapsed(b.label);
                              }
                            }}
                            aria-expanded={!collapsedGroups[(b.label ?? '').trim()]}
                          >
                            <button
                              type="button"
                              className="fields-groupbox-toggle"
                              onClick={(e) => { e.stopPropagation(); toggleGroupCollapsed(b.label); }}
                              aria-label={collapsedGroups[(b.label ?? '').trim()] ? 'Expand group' : 'Collapse group'}
                            >
                              {collapsedGroups[(b.label ?? '').trim()] ? '▶' : '▼'}
                            </button>
                            <span>{b.label}</span>
                          </div>
                        )}
                        {!collapsedGroups[(b.label ?? '').trim()] && (
                          <div className="fields-grid">
                            {b.rows}
                          </div>
                        )}
                      </div>
                    ));
                })()}
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type='submit' className="button-primary" style={{ flex: '0 0 85%' }}>Search</button>
            <button
              type='button'
              className="button-primary"
              style={{ flex: '0 0 15%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#e0e6ed' }}
              onClick={handleClearSearch}
              title="Clear search"
              aria-label="Clear search"
            >
              <span style={{ display: 'inline-flex' }}><EraserIcon size={20} /></span>
            </button>
          </div>
        </form>
        <div className="resizer" onMouseDown={handleMouseDown} />
        <div ref={scrollRef} className="results-pane">
          {error && <div className="error-text">{error}</div>}
          <DocumentGrid
            documents={results}
            fields={fields}
            documentTypes={documentTypes}
            resultColumns={resultColumns}
            headerInline={(
              <AddDocumentDropzone
                inline
                drawerId={drawerId}
                drawerName={drawerName}
                documentFields={documentOnlyFields}
                documentTypes={documentTypes}
                onImported={(doc) => {
                  // Only auto-insert when no criteria are active (default search / empty search).
                  const hasCriteria = criteria && Object.values(criteria).some(c => (c?.value ?? '').toString().trim().length > 0);
                  if (hasCriteria) return;
                  if (!doc) return;
                  setResults(prev => {
                    const id = Number((doc as any).id ?? (doc as any).Id);
                    if (!Number.isFinite(id)) return prev;
                    if (prev.some(d => Number((d as any).id ?? (d as any).Id) === id)) return prev;
                    return [doc as any, ...prev];
                  });
                }}
              />
            )}
            onColumnToggle={handleResultColumnToggle}
            showColumnSelector={showResultColumnSelector}
            onToggleColumnSelector={() => setShowResultColumnSelector(prev => !prev)}
            sortField={sortField}
            sortDir={sortDir}
            onHeaderClick={onHeaderClick}
            onEdit={documentActions.openEdit}
            onAudit={documentActions.openAudit}
            onDelete={async (doc) => await documentActions.deleteDocument(Number((doc as any)?.id ?? (doc as any)?.Id))}
            onAfterDelete={(deletedIds) => {
              // keep UI consistent even if parent callback chain changes
              setResults(prev => prev.filter(d => !deletedIds.includes(Number((d as any).id ?? (d as any).Id))));
            }}
            onMetadata={documentActions.openMetadata}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            hasMore={!!continuationToken}
            emptyMessage="No documents found"
            loadingFields={loadingFields}
            drawerId={drawerId}
            metadataOption={metadataOption}
            storagePrefix={'search'}
            onResetColumns={handleResetColumns}
            onPageSizeChange={handlePageSizeChange}
            previewHighlightText={contentsHighlightText}
          />

        </div>
      </div>

      <DocumentModals
        editDoc={documentActions.editDoc}
        editValues={documentActions.editValues}
        savingEdit={documentActions.savingEdit}
        editError={documentActions.editError}
        documentFields={documentOnlyFields}
        documentTypes={documentTypes}
        onEditValuesChange={documentActions.setEditValues}
        onEditCancel={documentActions.cancelEdit}
        onEditSave={documentActions.saveEdit}
        auditDoc={documentActions.auditDoc}
        audits={documentActions.audits}
        loadingAudits={documentActions.loadingAudits}
        auditError={documentActions.auditError}
        onAuditClose={documentActions.closeAudit}
        metadataOpen={documentActions.metadataOpen}
        metadataRows={documentActions.metadataRows}
        onMetadataClose={documentActions.closeMetadata}
      />
    </div>
  );
}
